import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Link, useParams } from "react-router-dom";
import AdminService from "src/services/admin.services";
import RestaurantDetails from "./RestaurantDetails";
import RestaurantTab from "./RestaurantTab";
import { request } from "../../../hooks/api";
import { Restaurant, Transaction } from "src/services/types";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import html2canvas from "html2canvas";
import DeliveryLocation from "../../deliverylocation/DeliveryLocation";
import BankDetailsTab from "./BankDetailsTab";
import WalletTab from "./WalletTab";
import FoodLoader from "src/components/Loader";
import RestaurantTransactions from "src/pages/finance/components/RestaurantTransactions";
import Modal from "./Modal2";
import CustomSelect from "../../../components/ui/CustomSelect";
import { AppLayout } from "src/components/layouts";

// Nigerian state coordinates (approximate center points)
const getStateFromCoordinates = (
  latitude: number,
  longitude: number
): string => {
  const stateCoordinates: {
    [key: string]: { lat: number; lng: number; radius: number };
  } = {
    Abuja: { lat: 9.082, lng: 7.3986, radius: 10.0 },
    Lagos: { lat: 6.5244, lng: 3.3792, radius: 10.0 },
    Kano: { lat: 11.9914, lng: 8.5311, radius: 10.0 },
    Kaduna: { lat: 10.5222, lng: 7.4384, radius: 10.0 },
    Katsina: { lat: 12.9914, lng: 7.6011, radius: 10.0 },
    Jigawa: { lat: 12.57, lng: 9.56, radius: 10.0 },
    Yobe: { lat: 12.0, lng: 11.5, radius: 10.0 },
    Borno: { lat: 11.8333, lng: 13.15, radius: 10.0 },
    Adamawa: { lat: 9.3273, lng: 12.3984, radius: 10.0 },
    Taraba: { lat: 8.8833, lng: 11.3667, radius: 10.0 },
    Gombe: { lat: 10.2897, lng: 11.1673, radius: 10.0 },
    Bauchi: { lat: 10.3158, lng: 9.8444, radius: 10.0 },
    Plateau: { lat: 9.8965, lng: 8.8583, radius: 10.0 },
    Nasarawa: { lat: 8.5, lng: 8.0, radius: 10.0 },
    Niger: { lat: 9.6, lng: 6.55, radius: 10.0 },
    Kebbi: { lat: 12.45, lng: 4.2, radius: 10.0 },
    Sokoto: { lat: 13.0667, lng: 5.2333, radius: 10.0 },
    Zamfara: { lat: 12.1667, lng: 6.1667, radius: 10.0 },
    Kogi: { lat: 7.8, lng: 6.7333, radius: 10.0 },
    Kwara: { lat: 8.5, lng: 4.55, radius: 10.0 },
    Oyo: { lat: 7.85, lng: 3.9333, radius: 10.0 },
    Osun: { lat: 7.75, lng: 4.5667, radius: 10.0 },
    Ondo: { lat: 7.25, lng: 5.2, radius: 10.0 },
    Ekiti: { lat: 7.6333, lng: 5.2167, radius: 10.0 },
    Ogun: { lat: 7.1667, lng: 3.35, radius: 10.0 },
    Edo: { lat: 6.3176, lng: 5.6145, radius: 10.0 },
    Delta: { lat: 5.5, lng: 5.75, radius: 10.0 },
    Rivers: { lat: 4.75, lng: 7.0, radius: 10.0 },
    Bayelsa: { lat: 4.75, lng: 6.25, radius: 10.0 },
    "Akwa Ibom": { lat: 5.0, lng: 7.85, radius: 10.0 },
    "Cross River": { lat: 5.5, lng: 8.0, radius: 10.0 },
    Ebonyi: { lat: 6.25, lng: 8.1, radius: 10.0 },
    Enugu: { lat: 6.45, lng: 7.5, radius: 10.0 },
    Anambra: { lat: 6.2, lng: 7.0, radius: 10.0 },
    Imo: { lat: 5.5, lng: 7.0, radius: 10.0 },
    Abia: { lat: 5.5, lng: 7.5, radius: 10.0 },
  };

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Find the closest state
  let closestState = "all";
  let minDistance = Infinity;

  for (const [state, coords] of Object.entries(stateCoordinates)) {
    const distance = calculateDistance(
      latitude,
      longitude,
      coords.lat,
      coords.lng
    );

    if (distance < minDistance && distance <= coords.radius) {
      minDistance = distance;
      closestState = state;
    }
  }
  return closestState;
};

interface OrderItem {
  restaurant_id: any;
  _id: string;
  order_code: string;
  restaurant_name: string;
  subtotal: number;
  total: number;
  delivery_fee: number;
  tax: number;
  date_delivered: string;
  items: {
    meal: {
      _id: string;
      name: string;
      description: string;
      restaurant_id: string;
      price: {
        amount: number;
        currency_code: string;
      };
      image_url?: string;
    };
    qty: number;
  }[];
  user: string;
  order_status: string;
  payment_method: string;
  pickup_time: string;
  agent: string;
  agent_delivered_order_upload: string;
  delivery_location: string;
  enroute_time: string;
  discount: {
    order: number;
    delivery: number;
  };
  voucher: number;
  created_at: string;
  updated_at: string;
  note: string;
  user_received_order_upload: string;
}

interface PayRestaurantProps {
  restaurantId: string;
  // Add other props here if needed
}

const adminService = new AdminService();
const ITEMS_PER_PAGE = 100;

const RestaurantDetailsWrapper: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
  const [filterDateRange, setFilterDateRange] = useState<string>("day");
  const [filterStatus, setFilterStatus] = useState<string>(""); // Updated to include "all" option
  const [selectedState, setSelectedState] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [tab, setTab] = useState<number>(0);
  const [restaurantTransactions, setRestaurantTransactions] = useState<
    Transaction[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedRef = useRef<boolean>(false);

  const fetchRestaurantDetails = useCallback(async () => {
    if (!restaurantId) {
      setError("Restaurant ID is missing");
      setLoading(false);
      return;
    }

    try {
      const response = await adminService.getRestaurantDetails(restaurantId);
      setRestaurant(response);
    } catch (error) {
      console.error("Error fetching restaurant details:", error);
      setError("Could not fetch restaurant details. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const fetchRestaurantTransactions = useCallback(async () => {
    if (!restaurantId) return;

    setLoading(true);
    try {
      const response = await adminService.getRestaurantTransactions(
        1,
        100,
        "",
        restaurantId,
        "",
        "",
        ""
      );
      setRestaurantTransactions(response);
    } catch (error) {
      console.error("Error fetching restaurant transactions:", error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) {
      setError("Restaurant ID is missing");
      setLoading(false);
      return;
    }

    fetchRestaurantTransactions();
    fetchRestaurantDetails();
  }, [restaurantId, fetchRestaurantDetails, fetchRestaurantTransactions]);

  // Orders fetching effect
  useEffect(() => {
    try {
      if (tab !== 1 || !restaurantId || !restaurant?.location?.Coordinates) {
        return;
      }

      // Reset fetch flag when tab changes
      if (tab === 1) {
        hasFetchedRef.current = false;
      }

      const fetchOrders = async () => {
        setOrdersLoading(true);
        try {
          const [longitude, latitude] = restaurant.location.Coordinates || [
            null,
            null,
          ];
          const maxDistance = 50000; // 50 km in meters

          // Dynamic API query with page number
          const query = `/shopping/order/search?page=1&restaurant_id=${restaurantId}&order_status=${filterStatus}&size=40&longitude=${longitude}&latitude=${latitude}&max_distance=${maxDistance}`;

          const response = await request(query, "GET", {}, true, false, false);

          if (Array.isArray(response)) {
            const filteredOrders = response.filter((order: OrderItem) => {
              // Apply state filter first
              if (selectedState !== "all") {
                // Use coordinates from restaurant.location.Coordinates if available
                if (restaurant?.location?.Coordinates) {
                  try {
                    const coordinates = restaurant.location.Coordinates;

                    // Coordinates are in [longitude, latitude] format
                    const [longitude, latitude] = coordinates;

                    // Get state from coordinates
                    const stateFromCoordinates = getStateFromCoordinates(
                      latitude,
                      longitude
                    );

                    if (
                      stateFromCoordinates !== "all" &&
                      stateFromCoordinates === selectedState
                    ) {
                      // Continue with search filter
                    } else {
                      return false; // State doesn't match
                    }
                  } catch (error) {
                    console.warn(
                      "Error processing restaurant coordinates:",
                      error
                    );
                    // Fallback to address-based filtering
                    const restaurantAddress =
                      restaurant?.address?.toLowerCase() || "";
                    if (
                      !restaurantAddress.includes(selectedState.toLowerCase())
                    ) {
                      return false; // State doesn't match
                    }
                  }
                } else {
                  // Fallback to address-based filtering if no coordinates
                  const restaurantAddress =
                    restaurant?.address?.toLowerCase() || "";
                  if (
                    !restaurantAddress.includes(selectedState.toLowerCase())
                  ) {
                    return false; // State doesn't match
                  }
                }
              }

              // Apply search filter
              const searchLower = searchTerm.toLowerCase();
              return (
                (order._id && order._id.toLowerCase().includes(searchLower)) ||
                (order.order_code &&
                  order.order_code.toLowerCase().includes(searchLower)) ||
                (order.restaurant_name &&
                  order.restaurant_name.toLowerCase().includes(searchLower)) ||
                (order.user &&
                  order.user.toLowerCase().includes(searchLower)) ||
                (order.agent && order.agent.toLowerCase().includes(searchLower))
              );
            });

            setOrders(filteredOrders);
            setTotalPages(Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
          } else {
            console.warn("Unexpected response format:", response);
            setOrders([]);
            setTotalPages(1);
          }
        } catch (err) {
          console.error("Failed to fetch orders:", err);
          setOrdersError("Failed to fetch orders");
        } finally {
          setOrdersLoading(false);
        }
      };

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Only fetch if we haven't already fetched or if search/filter changed
      if (!hasFetchedRef.current || searchTerm || filterStatus !== "all") {
        if (searchTerm) {
          searchTimeoutRef.current = setTimeout(() => {
            fetchOrders();
          }, 500);
        } else {
          fetchOrders();
        }
        hasFetchedRef.current = true;
      }

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    } catch (error) {
      console.error("Error in orders fetching effect:", error);
      setOrdersError("Failed to fetch orders");
    }
  }, [tab, restaurantId, filterStatus, selectedState, searchTerm]);

  const handleStatusChange = async (newStatus: boolean) => {
    if (restaurantId && restaurant) {
      try {
        await adminService.updateRestaurantStatus(restaurantId, newStatus);
        setRestaurant({ ...restaurant, is_activated: newStatus });
      } catch (error) {
        console.error("Error updating restaurant status:", error);
      }
    }
  };

  const openModal = (order: OrderItem) => {
    setSelectedOrder(order);
  };

  const closeModal = () => {
    setSelectedOrder(null);
  };

  const downloadInvoice = () => {
    const captureElement = document.getElementById("order-details");

    if (captureElement && selectedOrder) {
      // Add a small delay to ensure the modal is fully rendered
      setTimeout(() => {
        html2canvas(captureElement, {
          useCORS: true,
          scale: 1.5, // Reduced scale for better performance
          logging: true, // Enable logging for debugging
          backgroundColor: "#ffffff",
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: true,
          width: captureElement.scrollWidth,
          height: captureElement.scrollHeight,
        })
          .then((canvas) => {
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = `Order_${
              selectedOrder.order_code || selectedOrder._id
            }.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch((error) => {
            console.error("Error capturing the element:", error);

            // Fallback: Create a simple text-based invoice
            try {
              const invoiceContent = `
Order Invoice
=============
Order Code: ${selectedOrder.order_code}
Order ID: ${selectedOrder._id}
Restaurant: ${selectedOrder.restaurant_name}
Status: ${selectedOrder.order_status}
Date: ${new Date(selectedOrder.created_at).toLocaleDateString()}

Items:
${selectedOrder.items
  .map(
    (item) => `- ${item.meal.name} x${item.qty} @ ₦${item.meal.price.amount}`
  )
  .join("\n")}

Subtotal: ₦${selectedOrder.subtotal}
Delivery Fee: ₦${selectedOrder.delivery_fee}
Service Charge: ₦${selectedOrder.tax}
Total: ₦${selectedOrder.total}
              `;

              const blob = new Blob([invoiceContent], { type: "text/plain" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `Order_${
                selectedOrder.order_code || selectedOrder._id
              }.txt`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            } catch (fallbackError) {
              console.error("Fallback also failed:", fallbackError);
              alert("Failed to download invoice. Please try again.");
            }
          });
      }, 100);
    } else {
      if (!captureElement) {
        console.error("Capture element is missing");
        alert("Invoice element not found. Please try again.");
      }
      if (!selectedOrder) {
        console.error("Selected order is missing");
        alert("No order selected. Please select an order first.");
      }
    }
  };

  const printOrderDetails = () => {
    const printElement = document.getElementById("order-details");

    if (printElement && selectedOrder) {
      // Add a small delay to ensure the modal is fully rendered
      setTimeout(() => {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head>
                <title>Order Details - ${
                  selectedOrder?.order_code || "Order"
                }</title>
                <style>
                  body { 
                    font-family: Arial, sans-serif; 
                    margin: 20px; 
                    line-height: 1.6;
                  }
                  .header { 
                    text-align: center; 
                    margin-bottom: 30px; 
                    border-bottom: 2px solid #333;
                    padding-bottom: 10px;
                  }
                  .section { 
                    margin-bottom: 20px; 
                    page-break-inside: avoid;
                  }
                  .item { 
                    border-bottom: 1px solid #eee; 
                    padding: 10px 0; 
                  }
                  .total { 
                    font-weight: bold; 
                    font-size: 18px; 
                    margin-top: 20px; 
                    border-top: 2px solid #333;
                    padding-top: 10px;
                  }
                  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                  .card { 
                    border: 1px solid #ddd; 
                    padding: 15px; 
                    border-radius: 8px; 
                    background: #f9f9f9;
                  }
                  @media print { 
                    body { margin: 0; }
                    .no-print { display: none; }
                  }
                </style>
              </head>
              <body>
                ${printElement.innerHTML}
              </body>
            </html>
          `);
          printWindow.document.close();

          // Wait for content to load before printing
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
          };

          // Fallback if onload doesn't work
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
          }, 500);
        } else {
          console.error("Failed to open print window");
          alert(
            "Failed to open print window. Please check your popup blocker."
          );
        }
      }, 100);
    } else {
      if (!printElement) {
        console.error("Print element not found");
        alert("Print element not found. Please try again.");
      }
      if (!selectedOrder) {
        console.error("Selected order is missing");
        alert("No order selected. Please select an order first.");
      }
    }
  };

  const filterOrdersByDateRange = () => {
    const now = new Date();
    let filteredOrders = orders;

    if (filterDateRange === "day") {
      filteredOrders = orders.filter(
        (order) =>
          new Date(order.date_delivered).toDateString() === now.toDateString()
      );
    } else if (filterDateRange === "week") {
      const oneWeekAgo = new Date(now.setDate(now.getDate() - 7));
      filteredOrders = orders.filter(
        (order) => new Date(order.date_delivered) >= oneWeekAgo
      );
    } else if (filterDateRange === "month") {
      const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1));
      filteredOrders = orders.filter(
        (order) => new Date(order.date_delivered) >= oneMonthAgo
      );
    } else if (filterDateRange === "year") {
      const oneYearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
      filteredOrders = orders.filter(
        (order) => new Date(order.date_delivered) >= oneYearAgo
      );
    }

    if (filterDateRange === "all") return orders;

    return filteredOrders;
  };

  const handleDownload = () => {
    // Get filtered orders based on the current filter criteria
    const filteredOrders = filterOrdersByDateRange();

    const csvRows = [
      [
        "Order ID",
        "Order Code",
        "Restaurant",
        "Subtotal",
        "Total",
        "Delivery Fee",
        "Tax",
        "Date Delivered",
        "Items",
        "User",
        "Order Status",
        "Payment Method",
        "Pickup Time",
        "Agent",
        "Agent Delivered Order Upload",
        "Delivery Location",
        "Enroute Time",
        "Discount Order",
        "Discount Delivery",
        "Voucher",
        "Created At",
        "Updated At",
        "Note",
        "User Received Order Upload",
      ], // CSV header
      ...filteredOrders.map((order) => [
        order._id,
        order.order_code,
        order.restaurant_name,
        order.subtotal,
        order.total,
        order.delivery_fee,
        order.tax,
        new Date(order.date_delivered).toLocaleDateString(),
        order.items.map((item) => `${item.meal.name} (${item.qty})`).join(", "),
        order.user,
        order.order_status,
        order.payment_method,
        order.agent,
        order.agent_delivered_order_upload,
        order.delivery_location,
        // Check for default/invalid dates before formatting
        new Date(order.date_delivered).getFullYear() !== 1
          ? new Date(order.date_delivered).toLocaleDateString()
          : "",
        new Date(order.pickup_time).getFullYear() !== 1
          ? new Date(order.pickup_time).toLocaleDateString()
          : "",
        new Date(order.enroute_time).getFullYear() !== 1
          ? new Date(order.enroute_time).toLocaleDateString()
          : "",
        new Date(order.created_at).getFullYear() !== 1
          ? new Date(order.created_at).toLocaleDateString()
          : "",

        order.agent,
        order.agent_delivered_order_upload,
        order.delivery_location,
        order.discount?.order || "", // Handle missing discount
        order.discount?.delivery || "", // Handle missing delivery discount
        order.voucher || "", // Handle missing voucher

        order.note || "", // Handle missing notes
        order.user_received_order_upload || "", // Handle missing upload
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "filtered_orders.csv");
    document.body.appendChild(link);
    link.click();
  };

  if (loading)
    return (
      <AppLayout>
        <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
          <FoodLoader
            size="md"
            text="Fetching Restaurant Details"
            theme="amber"
            className="scale-75"
          />
        </div>
      </AppLayout>
    );
  if (error)
    return (
      <AppLayout>
        <div className="text-center text-red-500">{error}</div>
      </AppLayout>
    );

  async function checkRestaurantByEmail(email: string) {
    setLoading(true);
    try {
      const response = await adminService.checkRestaurant(email);
      if (response?.message === "restaurant verified successfully") {
        setModalMessage("Restaurant verified successfully");
        setModalType("success");
      } else {
        setModalMessage(response.error);
        setModalType("error");
      }
      setIsModalOpen(true);
      return response;
      // setTimeout(() => {
      //   window.location.reload();
      // }, 2000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Restaurant cannot be verified at the moment";
      setModalMessage(errorMessage);
      setModalType("error");
      setIsModalOpen(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-2 py-4">
        <RestaurantTab setTab={setTab} tab={tab} />
        <div className="mt-6">
          {restaurant && (
            <div className="flex flex-col items-center w-full">
              {tab === 0 && (
                <RestaurantDetails
                  restaurant={restaurant}
                  onStatusChange={handleStatusChange}
                  checkRestaurant={async ({ email }: { email: string }) => {
                    try {
                      const result = await checkRestaurantByEmail(email);
                      return result;
                    } catch (error) {
                      console.error("Failed to verify restaurant:", error);
                      return {
                        message: "Verification failed due to an error.",
                      };
                    }
                  }}
                />
              )}

              {tab === 1 && (
                <div className="w-full">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-xs font-medium">
                            Total Orders
                          </p>
                          <p className="text-2xl font-bold">{orders.length}</p>
                        </div>
                        <div className="bg-blue-400/30 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-xs font-medium">
                            Delivered
                          </p>
                          <p className="text-2xl font-bold">
                            {
                              orders.filter(
                                (o) => o.order_status === "delivered"
                              ).length
                            }
                          </p>
                        </div>
                        <div className="bg-green-400/30 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-yellow-100 text-xs font-medium">
                            Pending
                          </p>
                          <p className="text-2xl font-bold">
                            {
                              orders.filter((o) => o.order_status === "pending")
                                .length
                            }
                          </p>
                        </div>
                        <div className="bg-yellow-400/30 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-xs font-medium">
                            Total Revenue
                          </p>
                          <p className="text-2xl font-bold">
                            ₦
                            {orders
                              .reduce((sum, order) => sum + order.total, 0)
                              .toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-purple-400/30 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-4 border-b border-amber-100">
                      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
                        <div>
                          <h2 className="text-xl font-bold text-gray-800 mb-1">
                            Order History
                          </h2>
                          <p className="text-gray-600 text-sm">
                            Manage and track all restaurant orders
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleDownload}
                            disabled={orders.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Export Orders
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="px-6 py-4 bg-gray-50/50">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        {/* Search */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-4 w-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                          <input
                            type="text"
                            placeholder="Search orders..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all bg-white text-sm"
                          />
                        </div>

                        {/* Status Filter */}
                        <div>
                          <CustomSelect
                            value={filterStatus}
                            onChange={(value) =>
                              setFilterStatus(value as string)
                            }
                            options={[
                              { label: "All Statuses", value: "" },
                              { label: "Delivered", value: "delivered" },
                              { label: "Pending", value: "pending" },
                              { label: "Cancelled", value: "cancelled" },
                              { label: "Confirmed", value: "confirmed" },
                              { label: "Declined", value: "declined" },
                            ]}
                            placeholder="Filter by status"
                          />
                        </div>

                        {/* Date Filter */}
                        <div>
                          <CustomSelect
                            value={filterDateRange}
                            onChange={(value) =>
                              setFilterDateRange(value as string)
                            }
                            options={[
                              { label: "All Dates", value: "all" },
                              { label: "Today", value: "day" },
                              { label: "This Week", value: "week" },
                              { label: "This Month", value: "month" },
                              { label: "This Year", value: "year" },
                            ]}
                            placeholder="Filter by date"
                          />
                        </div>

                        {/* Clear Filters */}
                        <div>
                          <button
                            onClick={() => {
                              setSearchTerm("");
                              setFilterStatus("");
                              setFilterDateRange("all");
                            }}
                            className="w-full px-4 py-2 text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm"
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {ordersLoading ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-3"></div>
                          <p className="text-gray-600 text-sm">
                            Loading orders...
                          </p>
                        </div>
                      ) : ordersError ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                          <div className="flex items-center justify-center">
                            <svg
                              className="h-6 w-6 text-red-400 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <p className="text-red-700 text-sm">
                              {ordersError}
                            </p>
                          </div>
                        </div>
                      ) : orders.length > 0 ? (
                        <div className="space-y-3">
                          {orders.map((order) => (
                            <div
                              key={order._id}
                              onClick={() => openModal(order)}
                              className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:border-amber-200 transition-all duration-200 cursor-pointer group"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="bg-gradient-to-br from-amber-100 to-yellow-100 p-2 rounded-lg">
                                    <svg
                                      className="w-5 h-5 text-amber-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                  </div>
                                  <div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                                      {order.order_code}
                                    </h3>
                                    <p className="text-xs text-gray-500 font-mono">
                                      {order._id}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-gray-900">
                                      ₦{order.total.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {order.created_at
                                        ? new Date(
                                            order.created_at
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })
                                        : "N/A"}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        order.order_status === "delivered"
                                          ? "bg-green-100 text-green-800"
                                          : order.order_status === "pending"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : order.order_status === "cancelled"
                                          ? "bg-red-100 text-red-800"
                                          : order.order_status === "confirmed"
                                          ? "bg-blue-100 text-blue-800"
                                          : order.order_status === "declined"
                                          ? "bg-gray-100 text-gray-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {order.order_status
                                        ?.charAt(0)
                                        .toUpperCase() +
                                        order.order_status?.slice(1) ||
                                        "Unknown"}
                                    </span>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(order);
                                      }}
                                      className="flex items-center gap-1 text-amber-600 hover:text-amber-800 transition-colors group-hover:bg-amber-50 px-2 py-1 rounded-md"
                                    >
                                      <img
                                        src={removeRedEye}
                                        alt="View"
                                        className="w-3 h-3"
                                      />
                                      <span className="text-xs font-medium">
                                        View
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 max-w-sm mx-auto">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400 mb-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              No orders found
                            </h3>
                            <p className="text-gray-600 mb-4 text-sm">
                              {searchTerm ||
                              filterStatus ||
                              filterDateRange !== "all"
                                ? "Try adjusting your search or filter criteria."
                                : "This restaurant hasn't received any orders yet."}
                            </p>
                            {(searchTerm ||
                              filterStatus ||
                              filterDateRange !== "all") && (
                              <button
                                onClick={() => {
                                  setSearchTerm("");
                                  setFilterStatus("");
                                  setFilterDateRange("all");
                                }}
                                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm"
                              >
                                Clear All Filters
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div
              id="order-details"
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-yellow-50 px-8 py-6 border-b border-amber-200 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      Order Details
                    </h2>
                    <p className="text-gray-600 text-sm">
                      Order #{selectedOrder.order_code}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={downloadInvoice}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium text-sm"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Download Invoice
                      </button>
                      <button
                        onClick={printOrderDetails}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium text-sm"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                          />
                        </svg>
                        Print Order
                      </button>
                    </div>
                    <button
                      onClick={closeModal}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Order Information */}
                  <div className="space-y-6">
                    {/* Order Summary Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Order Summary
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700 font-medium">
                            Order ID:
                          </span>
                          <span className="text-blue-900 font-mono text-sm">
                            {selectedOrder._id}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700 font-medium">
                            Order Code:
                          </span>
                          <span className="text-blue-900 font-semibold">
                            {selectedOrder.order_code}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700 font-medium">
                            Status:
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedOrder.order_status === "delivered"
                                ? "bg-green-100 text-green-800"
                                : selectedOrder.order_status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : selectedOrder.order_status === "cancelled"
                                ? "bg-red-100 text-red-800"
                                : selectedOrder.order_status === "confirmed"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {selectedOrder.order_status
                              ?.charAt(0)
                              .toUpperCase() +
                              selectedOrder.order_status?.slice(1)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700 font-medium">
                            Payment Method:
                          </span>
                          <span className="text-blue-900">
                            {selectedOrder.payment_method}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Timestamps Card */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                      <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Timestamps
                      </h3>
                      <div className="space-y-3">
                        {selectedOrder.created_at && (
                          <div className="flex justify-between items-center">
                            <span className="text-purple-700 font-medium">
                              Created:
                            </span>
                            <span className="text-purple-900 text-sm">
                              {new Date(
                                selectedOrder.created_at
                              ).toLocaleDateString()}{" "}
                              {new Date(
                                selectedOrder.created_at
                              ).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                        {selectedOrder.pickup_time &&
                          new Date(selectedOrder.pickup_time).getFullYear() !==
                            1 && (
                            <div className="flex justify-between items-center">
                              <span className="text-purple-700 font-medium">
                                Pickup:
                              </span>
                              <span className="text-purple-900 text-sm">
                                {new Date(
                                  selectedOrder.pickup_time
                                ).toLocaleDateString()}{" "}
                                {new Date(
                                  selectedOrder.pickup_time
                                ).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                        {selectedOrder.enroute_time &&
                          new Date(selectedOrder.enroute_time).getFullYear() !==
                            1 && (
                            <div className="flex justify-between items-center">
                              <span className="text-purple-700 font-medium">
                                Enroute:
                              </span>
                              <span className="text-purple-900 text-sm">
                                {new Date(
                                  selectedOrder.enroute_time
                                ).toLocaleDateString()}{" "}
                                {new Date(
                                  selectedOrder.enroute_time
                                ).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                        {selectedOrder.date_delivered &&
                          new Date(
                            selectedOrder.date_delivered
                          ).getFullYear() !== 1 && (
                            <div className="flex justify-between items-center">
                              <span className="text-purple-700 font-medium">
                                Delivered:
                              </span>
                              <span className="text-purple-900 text-sm">
                                {new Date(
                                  selectedOrder.date_delivered
                                ).toLocaleDateString()}{" "}
                                {new Date(
                                  selectedOrder.date_delivered
                                ).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Links Card */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                      <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                        Quick Links
                      </h3>
                      <div className="space-y-3">
                        <Link
                          to={`/pr-admin/agents/agent-details/${selectedOrder.agent}`}
                          className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <span className="text-green-700 font-medium">
                            View Agent
                          </span>
                          <img
                            src={removeRedEye}
                            alt="View"
                            className="w-4 h-4"
                          />
                        </Link>
                        <Link
                          to={`/pr-admin/users/user-details/${selectedOrder.user}`}
                          className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <span className="text-green-700 font-medium">
                            View User
                          </span>
                          <img
                            src={removeRedEye}
                            alt="View"
                            className="w-4 h-4"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Items and Pricing */}
                  <div className="space-y-6">
                    {/* Items Card */}
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-2xl p-6 border border-amber-200">
                      <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"
                          />
                        </svg>
                        Order Items
                      </h3>
                      <div className="space-y-4">
                        {selectedOrder.items.map((item, index) => (
                          <div
                            key={item.meal._id}
                            className="bg-white rounded-xl p-4 border border-amber-200"
                          >
                            <div className="flex items-start gap-4">
                              {item.meal.image_url && (
                                <img
                                  src={item.meal.image_url}
                                  alt={item.meal.name}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-1">
                                  {item.meal.name}
                                </h4>
                                <p className="text-sm text-gray-600 mb-2">
                                  {item.meal.description}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-amber-700 font-medium">
                                    ₦{item.meal.price.amount}
                                  </span>
                                  <span className="text-gray-500 text-sm">
                                    Qty: {item.qty}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pricing Card */}
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                      <h3 className="text-lg font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                          />
                        </svg>
                        Pricing Details
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-indigo-700 font-medium">
                            Subtotal:
                          </span>
                          <span className="text-indigo-900">
                            ₦{selectedOrder.subtotal}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-indigo-700 font-medium">
                            Delivery Fee:
                          </span>
                          <span className="text-indigo-900">
                            ₦{selectedOrder.delivery_fee}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-indigo-700 font-medium">
                            Service Charge:
                          </span>
                          <span className="text-indigo-900">
                            ₦{selectedOrder.tax}
                          </span>
                        </div>
                        {selectedOrder.discount.order > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-700 font-medium">
                              Order Discount:
                            </span>
                            <span className="text-indigo-900">
                              -₦{selectedOrder.discount.order}
                            </span>
                          </div>
                        )}
                        {selectedOrder.discount.delivery > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-700 font-medium">
                              Delivery Discount:
                            </span>
                            <span className="text-indigo-900">
                              -₦{selectedOrder.discount.delivery}
                            </span>
                          </div>
                        )}
                        {selectedOrder.voucher > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-700 font-medium">
                              Voucher:
                            </span>
                            <span className="text-indigo-900">
                              -₦{selectedOrder.voucher}
                            </span>
                          </div>
                        )}
                        <div className="border-t border-indigo-200 pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-900 font-bold text-lg">
                              Total:
                            </span>
                            <span className="text-indigo-900 font-bold text-xl">
                              ₦{selectedOrder.total}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Info Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Additional Information
                      </h3>
                      <div className="space-y-3">
                        {selectedOrder.note && (
                          <div>
                            <span className="text-gray-700 font-medium">
                              Note:
                            </span>
                            <p className="text-gray-600 text-sm mt-1">
                              {selectedOrder.note}
                            </p>
                          </div>
                        )}
                        {selectedOrder.agent_delivered_order_upload && (
                          <div>
                            <span className="text-gray-700 font-medium">
                              Agent Upload:
                            </span>
                            <p className="text-gray-600 text-sm mt-1">
                              {selectedOrder.agent_delivered_order_upload}
                            </p>
                          </div>
                        )}
                        {selectedOrder.user_received_order_upload && (
                          <div>
                            <span className="text-gray-700 font-medium">
                              User Upload:
                            </span>
                            <p className="text-gray-600 text-sm mt-1">
                              {selectedOrder.user_received_order_upload}
                            </p>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-700 font-medium">
                            Restaurant:
                          </span>
                          <p className="text-gray-600 text-sm mt-1">
                            {selectedOrder.restaurant_name}
                          </p>
                        </div>
                        {selectedOrder && (
                          <div>
                            <span className="text-gray-700 font-medium">
                              Delivery Location:
                            </span>
                            <div className="mt-1">
                              <DeliveryLocation
                                locationId={
                                  selectedOrder.delivery_location as string
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 2 && (
          <div>
            {/* Other UI components */}
            {restaurantId && <BankDetailsTab restaurantId={restaurantId} />}
            {/* Continue with your orders table */}
          </div>
        )}

        {tab === 3 && (
          <div>{restaurantId && <WalletTab restaurantId={restaurantId} />}</div>
        )}

        {tab === 4 && (
          <RestaurantTransactions
            transactions={restaurantTransactions}
            loading={loading}
            error={error}
          />
        )}
      </div>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          type={modalType}
          message={modalMessage}
        />
      )}
    </AppLayout>
  );
};

export default RestaurantDetailsWrapper;
